import { Settlement } from '../../../models/Entities/Settlements/Settlement';
import { SettlementDebt } from '../../../models/Entities/SettlementDebt/SettlementDebt';

/**
 * Colecciones que se cargan con pedidos propios (resúmenes, detalles, deudas)
 * y no vienen, o vienen vacías, en el GET de la liquidación.
 */
const CHILD_COLLECTIONS: (keyof Settlement)[] = ['resumes', 'details', 'debts', 'totals'];

const hasItems = (value: unknown): boolean => Array.isArray(value) && value.length > 0;

/**
 * Mezcla una liquidación recién recibida sobre la que ya estaba en el store.
 *
 * El GET de la liquidación se dispara desde varios componentes a la vez al
 * abrir la página; si cada respuesta reemplazara el objeto entero, la última
 * en llegar borraría los resúmenes o deudas que otro pedido ya había cargado.
 * Se conservan las colecciones ya cargadas cuando la respuesta no las trae, y
 * los flags de carga en curso (isGetting*), que la API no conoce.
 */
export const mergeSettlement = (previous: Settlement | undefined, incoming: Settlement): Settlement => {
    if (!previous) return incoming;

    // Un campo que la respuesta no trae (undefined o null) no borra el que ya
    // estaba: el PUT devuelve un objeto más chico que el GET.
    const provided: Partial<Settlement> = {};
    (Object.keys(incoming) as (keyof Settlement)[]).forEach((key) => {
        const value = incoming[key];
        if (value !== undefined && value !== null) (provided as any)[key] = value;
    });

    const merged: Settlement = { ...previous, ...provided };

    CHILD_COLLECTIONS.forEach((key) => {
        if (!hasItems(incoming[key]) && hasItems(previous[key])) {
            (merged as any)[key] = previous[key];
        }
    });

    return merged;
};

/** Reemplaza (o agrega) la liquidación en la lista, mezclándola con la existente. */
export const upsertSettlement = (list: Settlement[] | undefined, incoming: Settlement): Settlement[] => {
    const previous = (list || []).find((item) => item.id === incoming.id);
    return [...(list || []).filter((item) => item.id !== incoming.id), mergeSettlement(previous, incoming)];
};

/**
 * Las deudas de una liquidación llegan por dos caminos: el vínculo de la API
 * de liquidaciones y el detalle de cashflow (por id de liquidación). Cada
 * respuesta refresca lo suyo sin borrar lo que trajo la otra: si el vínculo
 * llega vacío o tarde, la deuda que cashflow ya devolvió sigue en pantalla.
 */
export const mergeDebtLinks = (previous: SettlementDebt[] | undefined, links: SettlementDebt[] | undefined): SettlementDebt[] => {
    const before = previous ?? [];
    const merged = (links ?? []).map((link) => {
        const known = before.find((item) => item.debtId === link.debtId);
        return known?.CashFlowDebt && !link.CashFlowDebt ? { ...link, CashFlowDebt: known.CashFlowDebt } : link;
    });
    const linked = new Set(merged.map((item) => item.debtId));
    before
        .filter((item) => !!item.CashFlowDebt && !linked.has(item.debtId))
        .forEach((item) => merged.push(item));
    return merged;
};
